import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify, useUpdate } from 'react-admin';
import { UserPlus } from 'lucide-react';
import { DialogCloseButton } from '../misc/DialogCloseButton';
import { Contact } from '../types';

interface ContactEditDialogProps {
    open: boolean;
    onClose: () => void;
    contact: Contact;
}

export const ContactEditDialog = ({
    open,
    onClose,
    contact,
}: ContactEditDialogProps) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [title, setTitle] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    const [update, { isPending }] = useUpdate();
    const notify = useNotify();

    // Populate fields when dialog opens or contact changes
    useEffect(() => {
        if (open && contact) {
            setFirstName(contact.first_name || '');
            setLastName(contact.last_name || '');
            setTitle(contact.title || '');
            setEmail(contact.email_jsonb?.[0]?.email || '');
            setPhone(contact.phone_jsonb?.[0]?.number || '');
        }
    }, [open, contact]);

    const handleSubmit = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            notify('First and last name are required', {
                type: 'warning',
            });
            return;
        }

        try {
            const contactData: Record<string, unknown> = {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                title: title.trim() || null,
            };

            // Handle email_jsonb — preserve existing type
            const emailVal = email.trim();
            if (emailVal) {
                contactData.email_jsonb = [
                    {
                        email: emailVal,
                        type: contact.email_jsonb?.[0]?.type || 'Work',
                    },
                ];
            } else {
                contactData.email_jsonb = [];
            }

            // Handle phone_jsonb — preserve existing type
            const phoneVal = phone.trim();
            if (phoneVal) {
                contactData.phone_jsonb = [
                    {
                        number: phoneVal,
                        type: contact.phone_jsonb?.[0]?.type || 'Work',
                    },
                ];
            } else {
                contactData.phone_jsonb = [];
            }

            await update(
                'contacts',
                {
                    id: contact.id,
                    data: contactData,
                    previousData: contact,
                },
                { returnPromise: true }
            );

            notify('Contact updated successfully', {
                type: 'success',
            });
            onClose();
        } catch {
            notify('Error updating contact', {
                type: 'error',
            });
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#cbd5e1',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#10b981',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: '#64748b',
            '&.Mui-focused': {
                color: '#10b981',
            },
        },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    },
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background:
                        'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                    borderBottom: '1px solid #d1fae5',
                    px: 3,
                    py: 2.5,
                    position: 'relative',
                }}
            >
                <DialogCloseButton onClose={onClose} top={12} right={12} />
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: '#d1fae5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <UserPlus size={20} color="#059669" />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: '#0f172a',
                            }}
                        >
                            Edit Contact
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                color: '#64748b',
                            }}
                        >
                            Update contact information
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5 }}>
                {/* Contact Information Section */}
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 2,
                    }}
                >
                    Contact Information
                </Typography>

                <Stack spacing={2}>
                    {/* Name Row */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="First Name"
                            required
                            fullWidth
                            size="small"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Last Name"
                            required
                            fullWidth
                            size="small"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            sx={fieldSx}
                        />
                    </Stack>

                    {/* Role/Title */}
                    <TextField
                        label="Role / Title"
                        fullWidth
                        size="small"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        sx={fieldSx}
                    />

                    {/* Phone & Email Row */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Phone Number"
                            fullWidth
                            size="small"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Email Address"
                            fullWidth
                            size="small"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            sx={fieldSx}
                        />
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid #e2e8f0',
                    gap: 1.5,
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: 'none',
                        color: '#64748b',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': {
                            backgroundColor: '#f1f5f9',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isPending}
                    startIcon={
                        isPending ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <UserPlus size={16} />
                        )
                    }
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#10b981',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        px: 2.5,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#059669',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#86efac',
                        },
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};
